// SkuGeneratorDialog — Template-only SKU picker (iter_138, Phase 3 rewrite).
//
// The legacy "Family" concept has been retired; every 品番ルール is now a Template
// with segments baked in. This dialog:
//   1. Lists all templates (optionally filtered by app_code/department/type_code)
//   2. Lets user pick one, pre-fills segment values from the template's presets
//   3. Renders inputs for any non-preset, non-auto segments
//   4. Live preview via /api/sku/preview  { template_id, values }
//   5. Issues via /api/sku/issue        { template_id, values }
//
// Backwards-compat prop: `defaultFamily` is now treated as `defaultTemplateCode`
// so existing callers that passed "purchased" etc. keep working — if a template
// with that code doesn't exist, we just fall back to "no template selected".
//
// Usage: <SkuGeneratorDialog trigger={<Button>...</Button>} onIssued={(sku) => ...} />
import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem, SelectGroup, SelectLabel,
} from '@/components/ui/select';
import { SearchableSelect } from '@/components/ui/searchable-select';
import {
  Hash, Sparkle, ArrowsClockwise, CheckCircle, LockSimple, Bookmark,
} from '@phosphor-icons/react';
import { skuMajorTitle, groupTemplatesByName } from '@/lib/skuDisplay';
import { filterLedgerLocked } from '@/lib/ledgerPrefixes';
import { useAuth, isAdmin } from '@/auth/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const NO_TEMPLATE = '__none__';

// Segment kinds that resolve automatically at issue time (no user input).
// Kept in sync with routers/sku.py::_build_sku.
const AUTO_KINDS = new Set(['literal', 'year2', 'year4', 'month', 'counter']);

export function SkuGeneratorDialog({
  trigger, defaultFamily = '', onIssued,
  open: controlledOpen, onOpenChange, initialTemplateCode,
  defaultApp = '',
  deferred = false,   // true: 発番せずプレビューを返す（保存時に正式発番）
}) {
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
  const isControlled = controlledOpen !== undefined;
  const open = isControlled ? controlledOpen : uncontrolledOpen;
  const setOpen = isControlled ? onOpenChange : setUncontrolledOpen;

  const [templates, setTemplates] = useState([]);
  const [categories, setCategories] = useState([]);
  const [subs, setSubs] = useState([]);
  const [units, setUnits] = useState([]);
  const [templateCode, setTemplateCode] = useState(NO_TEMPLATE);
  const [values, setValues] = useState({});
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [issued, setIssued] = useState(null);
  // 番号の取り方: 'new' = 次の連番 / 'reuse' = 再利用可の番号（管理者のみ）
  const { user } = useAuth();
  const admin = isAdmin(user);
  const [mode, setMode] = useState('new');
  const [reusable, setReusable] = useState([]);
  const [reuseSku, setReuseSku] = useState('');

  const currentTemplate = useMemo(
    () => templates.find((t) => t.code === templateCode) || null,
    [templates, templateCode],
  );
  const lockedKeys = currentTemplate?.locked_keys || [];
  const filteredSubs = useMemo(
    () => (values.category ? subs.filter((s) => s.category_code === values.category) : []),
    [subs, values.category],
  );
  const templatesByName = useMemo(() => groupTemplatesByName(templates), [templates]);

  // Load templates + masters when dialog opens
  useEffect(() => {
    if (!open) return;
    let alive = true;
    (async () => {
      try {
        const params = defaultApp ? { app_code: defaultApp } : {};
        const [t, c, s, u] = await Promise.all([
          axios.get(`${API}/sku/templates`, { params, withCredentials: true }),
          axios.get(`${API}/sku/categories`, { withCredentials: true }),
          axios.get(`${API}/sku/subcategories`, { withCredentials: true }),
          axios.get(`${API}/sku/unit-codes`, { withCredentials: true }),
        ]);
        if (!alive) return;
        // 20260610 · 台帳が自前カウンターで採番するルールはここからも発番させない
        // （ロック対象は GET /api/sku/ledger-counters が正本）。
        setTemplates(await filterLedgerLocked(t.data || []));
        setCategories(c.data || []);
        setSubs(s.data || []);
        setUnits(u.data || []);
        // Auto-apply initial template (from prop). Falls back cleanly if not found.
        const wanted = initialTemplateCode || defaultFamily || '';
        const tpl = wanted ? (t.data || []).find((x) => x.code === wanted) : null;
        if (tpl) {
          applyTemplateInternal(tpl);
        } else {
          setTemplateCode(NO_TEMPLATE);
          setValues({});
        }
      } catch {
        toast.error('マスターデータの読み込みに失敗しました');
      }
    })();
    return () => { alive = false; };
  }, [open, initialTemplateCode, defaultFamily, defaultApp]);

  const applyTemplateInternal = (tpl) => {
    const initial = {};
    for (const seg of tpl.segments || []) {
      if (seg.default !== undefined) initial[seg.key] = seg.default;
    }
    setValues({ ...initial, ...(tpl.values || {}) });
    setTemplateCode(tpl.code);
    setIssued(null);
    setError(null);
  };

  const applyTemplate = (code) => {
    if (code === NO_TEMPLATE) {
      setTemplateCode(NO_TEMPLATE);
      setValues({});
      setPreview(null);
      setError(null);
      return;
    }
    const tpl = templates.find((t) => t.code === code);
    if (tpl) applyTemplateInternal(tpl);
  };

  // Live preview (debounced)
  useEffect(() => {
    if (!currentTemplate) { setPreview(null); return; }
    const timer = setTimeout(async () => {
      try {
        const r = await axios.post(
          `${API}/sku/preview`,
          { template_id: currentTemplate.id, values },
          { withCredentials: true },
        );
        setPreview(r.data);
        setError(null);
      } catch (e) {
        setPreview(null);
        setError(e?.response?.data?.detail || null);
      }
    }, 250);
    return () => clearTimeout(timer);
  }, [currentTemplate, values]);

  // 再利用可の番号（管理者のみ・選択中の品番ルール単位）
  useEffect(() => {
    if (!open || !admin || !currentTemplate) { setReusable([]); return; }
    axios.get(`${API}/sku/numbers/reusable`, {
      params: { template_id: currentTemplate.id }, withCredentials: true,
    }).then((r) => setReusable(r.data?.items || [])).catch(() => setReusable([]));
    setReuseSku('');
    setMode('new');
  }, [open, admin, currentTemplate]);

  const setVal = (k) => (v) => {
    if (lockedKeys.includes(k)) return;
    setValues((p) => ({ ...p, [k]: v }));
  };

  const handleIssue = async () => {
    if (!currentTemplate) return;
    setBusy(true);
    try {
      if (mode === 'reuse') {
        if (!reuseSku) { toast.error('再利用する番号を選択してください'); return; }
        if (deferred) {
          // 保存時に正式発番（キャンセル時は再利用可のまま残る）
          setIssued({ sku: reuseSku, preview: true, template_name: currentTemplate.name });
          toast.success(`再利用予定: ${reuseSku} — 保存時に正式発番されます`);
          if (onIssued) onIssued(reuseSku, { pending: { template_id: currentTemplate.id, values, reuse_sku: reuseSku } });
        } else {
          const r = await axios.post(
            `${API}/sku/issue`,
            { template_id: currentTemplate.id, values, reuse_sku: reuseSku },
            { withCredentials: true },
          );
          setIssued(r.data);
          toast.success(`解放番号 ${r.data.sku} を再利用して発番しました`);
          if (onIssued) onIssued(r.data.sku, {});
        }
      } else if (deferred) {
        // プレビューのみ — 保存時に正式発番（キャンセル時は消費されない）
        const r = await axios.post(
          `${API}/sku/preview`,
          { template_id: currentTemplate.id, values },
          { withCredentials: true },
        );
        setIssued({ ...r.data, preview: true });
        toast.success(`発番予定: ${r.data.sku} — 保存時に正式発番されます`);
        if (onIssued) onIssued(r.data.sku, { pending: { template_id: currentTemplate.id, values } });
      } else {
        const r = await axios.post(
          `${API}/sku/issue`,
          { template_id: currentTemplate.id, values },
          { withCredentials: true },
        );
        setIssued(r.data);
        toast.success(`品番 ${r.data.sku} を発番しました`);
        if (onIssued) onIssued(r.data.sku, {});
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || '発番に失敗しました');
    } finally {
      setBusy(false);
    }
  };

  const isLocked = (key) => lockedKeys.includes(key);

  const renderSegment = (seg) => {
    const key = seg.key;
    const locked = isLocked(key);
    const lockBadge = locked && (
      <LockSimple weight="fill" size={11} className="inline-block ml-1 text-amber-500" />
    );
    // Auto-resolved kinds — show a read-only placeholder pill
    if (['literal', 'year2', 'year4', 'month', 'counter'].includes(seg.kind)) {
      const label =
        seg.kind === 'year2' ? '年 (自動)' :
        seg.kind === 'year4' ? '年 YYYY (自動)' :
        seg.kind === 'month' ? '月 (自動)' :
        seg.kind === 'counter' ? '連番 (自動)' :
        seg.label;
      const val = seg.kind === 'literal' ? seg.value : '——';
      return (
        <div key={key} className="opacity-80">
          <Label className="text-[10px] font-semibold text-zinc-500 uppercase tracking-wider">{label}</Label>
          <div className="mt-1 h-10 px-3 rounded-md bg-zinc-50 border border-zinc-200 text-sm font-mono flex items-center text-zinc-500">
            {val}
          </div>
        </div>
      );
    }
    if (seg.kind === 'unit_code') {
      return (
        <div key={key}>
          <Label className="text-xs font-semibold text-zinc-600">{seg.label}{lockBadge}</Label>
          <SearchableSelect
            value={values[key] || ''}
            onValueChange={setVal(key)}
            disabled={locked}
            triggerClassName={`mt-1 ${locked ? 'bg-amber-50 border-amber-200' : ''}`}
            testid={`sku-seg-${key}`}
            placeholder="選択…"
            searchPlaceholder="コード・番号で検索"
            options={units.map((u) => ({
              value: u.code,
              primary: u.code,
              secondary: `#${u.unit_no}${u.alt_code ? ` / ${u.alt_code}` : ''}`,
            }))}
          />
        </div>
      );
    }
    if (seg.kind === 'category') {
      return (
        <div key={key}>
          <Label className="text-xs font-semibold text-zinc-600">{seg.label}{lockBadge}</Label>
          <SearchableSelect
            value={values[key] || ''}
            onValueChange={(v) => !locked && setValues((p) => ({ ...p, [key]: v, subcategory: '' }))}
            disabled={locked}
            triggerClassName={`mt-1 ${locked ? 'bg-amber-50 border-amber-200' : ''}`}
            testid={`sku-seg-${key}`}
            placeholder="選択…"
            searchPlaceholder="コード・名称で検索"
            options={categories.map((c) => ({ value: c.code, primary: c.code, secondary: c.name }))}
          />
        </div>
      );
    }
    if (seg.kind === 'subcategory') {
      return (
        <div key={key}>
          <Label className="text-xs font-semibold text-zinc-600">{seg.label}{lockBadge}</Label>
          <SearchableSelect
            value={values[key] || ''}
            onValueChange={setVal(key)}
            disabled={locked || !values.category}
            triggerClassName={`mt-1 ${locked ? 'bg-amber-50 border-amber-200' : ''}`}
            testid={`sku-seg-${key}`}
            placeholder={values.category ? '選択…' : '先に大分類を選択'}
            searchPlaceholder="コード・名称で検索"
            options={filteredSubs.map((s) => ({ value: s.code, primary: s.code, secondary: s.name }))}
          />
        </div>
      );
    }
    if (seg.kind === 'rev') {
      return (
        <div key={key}>
          <Label className="text-xs font-semibold text-zinc-600">{seg.label}{lockBadge}</Label>
          <Input
            value={values[key] || seg.default || 'X'}
            onChange={(e) => setVal(key)(e.target.value.toUpperCase().slice(0, 1))}
            maxLength={1} disabled={locked}
            className={`h-10 mt-1 font-mono uppercase ${locked ? 'bg-amber-50 border-amber-200' : ''}`}
            data-testid={`sku-seg-${key}`}
          />
        </div>
      );
    }
    // Fallback: input (free-form)
    return (
      <div key={key}>
        <Label className="text-xs font-semibold text-zinc-600">{seg.label}{lockBadge}</Label>
        <Input
          value={values[key] ?? seg.default ?? ''}
          onChange={(e) => setVal(key)(e.target.value.toUpperCase().slice(0, seg.width))}
          maxLength={seg.width} disabled={locked}
          className={`h-10 mt-1 font-mono uppercase ${locked ? 'bg-amber-50 border-amber-200' : ''}`}
          data-testid={`sku-seg-${key}`}
          placeholder={seg.width ? '_'.repeat(seg.width) : ''}
        />
      </div>
    );
  };

  const dialogContent = (
    <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="sku-generator-dialog">
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2">
          <Hash weight="duotone" className="text-blue-600" size={22} />
          <span>品番自動採番</span>
        </DialogTitle>
        <DialogDescription>
          品番ルールを選んで、必要なら各セグメントを入力すると、自動的に連番を割り当てて品番を発行します。
        </DialogDescription>
      </DialogHeader>

      {issued ? (
        <div className="space-y-4">
          <div className="rounded-lg bg-teal-700 p-6 text-white shadow-lg">
            <div className="flex items-center gap-2 text-sm font-semibold opacity-90">
              <CheckCircle weight="fill" size={20} />
              <span>発番完了</span>
            </div>
            <div className="mt-2 text-4xl font-mono font-bold tracking-wider" data-testid="sku-issued-value">{issued.sku}</div>
            <div className="mt-2 text-xs opacity-80">{issued.template_name || issued.template_code} · バケット: {issued.bucket}</div>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setIssued(null)} data-testid="sku-issue-again">続けて発番</Button>
            <Button onClick={() => setOpen(false)} data-testid="sku-issue-close">閉じる</Button>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          {/* Template picker */}
          {templates.length === 0 ? (
            <div className="rounded-xl border-2 border-dashed border-amber-200 bg-amber-50/40 p-6 text-center">
              <div className="text-sm text-zinc-600">
                発番可能な品番ルールがありません。<br />
                <span className="text-xs text-zinc-500">
                  管理者に品番ルールの作成を依頼するか、
                  <a href="/settings/sku-master" className="underline text-blue-600 mx-1">設定 → 品番マスター</a>
                  でルールを追加してください。
                </span>
              </div>
            </div>
          ) : (
            <>
              <div>
                <Label className="text-xs font-semibold text-zinc-600 mb-2 flex items-center gap-1">
                  <Bookmark weight="duotone" size={14} className="text-amber-500" />
                  品番ルール
                </Label>
                <Select value={templateCode} onValueChange={applyTemplate}>
                  <SelectTrigger className="h-10" data-testid="sku-template-picker">
                    <SelectValue placeholder="品番ルールを選択…" />
                  </SelectTrigger>
                  <SelectContent className="max-h-80">
                    <SelectItem value={NO_TEMPLATE}>— 未選択 —</SelectItem>
                    {templatesByName.map((g) => (
                      <SelectGroup key={g.name || '__flat__'}>
                        {g.name && (
                          <SelectLabel className="text-[10px] text-zinc-400">{g.name}</SelectLabel>
                        )}
                        {g.items.map((t) => (
                          <SelectItem key={t.code} value={t.code} data-testid={`sku-template-option-${t.code}`}>
                            <span className="font-semibold">{skuMajorTitle(t)}</span>
                            <span className="ml-2 text-xs text-zinc-500">{t.code}</span>
                          </SelectItem>
                        ))}
                      </SelectGroup>
                    ))}
                  </SelectContent>
                </Select>
                {lockedKeys.length > 0 && (
                  <div className="mt-2 flex items-center gap-2 text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-2 py-1.5">
                    <LockSimple weight="fill" size={12} />
                    <span>
                      ロック済みキー: <b className="font-mono">{lockedKeys.join(', ')}</b>
                    </span>
                  </div>
                )}
              </div>

              {/* Preview */}
              <div className={`rounded-xl border-2 border-dashed p-4 ${error ? 'border-rose-300 bg-rose-50' : 'border-blue-200 bg-blue-50'}`}>
                <div className="text-[11px] font-semibold uppercase tracking-wider text-zinc-500">プレビュー</div>
                <div className="mt-1 text-3xl font-mono font-bold text-zinc-800" data-testid="sku-preview-value">
                  {mode === 'reuse' ? (reuseSku || '——') : (preview?.sku || '——')}
                </div>
                {error && <div className="mt-2 text-xs text-rose-700" data-testid="sku-preview-error">{error}</div>}
                {preview?.bucket && !error && (
                  <div className="mt-2 text-[11px] text-zinc-500">
                    バケット: <span className="font-mono">{preview.bucket}</span>
                    {preview.resolved?._serial_int !== undefined && (
                      <span className="ml-3">次の連番: <b>#{preview.resolved._serial_int + 1}</b></span>
                    )}
                  </div>
                )}
              </div>

              {/* 番号の取り方（管理者のみ再利用を選択できる） */}
              {admin && currentTemplate && (
                <div className="rounded-xl border border-violet-200 bg-violet-50/50 p-3 space-y-2" data-testid="sku-reuse-mode">
                  <div className="flex items-center gap-4 text-xs font-semibold text-zinc-700">
                    <label className="inline-flex items-center gap-1.5 cursor-pointer">
                      <input type="radio" checked={mode === 'new'} onChange={() => setMode('new')}
                        className="accent-blue-600" data-testid="sku-mode-new" />
                      新規番号（次の連番）
                    </label>
                    <label className={`inline-flex items-center gap-1.5 ${reusable.length ? 'cursor-pointer' : 'opacity-50'}`}>
                      <input type="radio" checked={mode === 'reuse'} disabled={!reusable.length}
                        onChange={() => setMode('reuse')} className="accent-violet-600" data-testid="sku-mode-reuse" />
                      再利用可の番号から選ぶ（{reusable.length}）
                    </label>
                  </div>
                  {mode === 'reuse' && (
                    <select value={reuseSku} onChange={(e) => setReuseSku(e.target.value)}
                      className="h-9 w-full rounded-md border border-violet-200 bg-white px-2 text-sm font-mono"
                      data-testid="sku-reuse-select">
                      <option value="">番号を選択…</option>
                      {reusable.map((r) => <option key={r.sku} value={r.sku}>{r.sku}</option>)}
                    </select>
                  )}
                  <p className="text-[10px] text-zinc-500">
                    解放された番号は自動では再利用されません。管理者がここで明示的に選んだ場合のみ再利用されます。
                  </p>
                </div>
              )}

              {/* Segment inputs (only for non-auto kinds) */}
              {currentTemplate && (
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {(currentTemplate.segments || []).map(renderSegment)}
                </div>
              )}

              <div className="flex items-center justify-between pt-2">
                <div className="text-[11px] text-zinc-500">{currentTemplate?.description || ''}</div>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => setOpen(false)} data-testid="sku-generator-cancel">キャンセル</Button>
                  <Button
                    onClick={handleIssue}
                    disabled={busy || !currentTemplate || (mode === 'reuse' ? !reuseSku : (!preview?.sku || !!error))}
                    className="bg-teal-700 hover:bg-teal-800"
                    data-testid="sku-generator-issue"
                  >
                    {busy ? <ArrowsClockwise className="animate-spin" size={16} /> : <Sparkle weight="fill" size={16} />}
                    <span className="ml-2">発番する</span>
                  </Button>
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </DialogContent>
  );

  if (isControlled) {
    return <Dialog open={open} onOpenChange={setOpen}>{dialogContent}</Dialog>;
  }
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      {trigger && <DialogTrigger asChild>{trigger}</DialogTrigger>}
      {dialogContent}
    </Dialog>
  );
}

export default SkuGeneratorDialog;
